import {
  Button,
  DateBox,
  ScrollView,
  SelectBox,
  TextBox,
} from "devextreme-react";
import {
  DataGrid,
  Item as DataGridItem,
  FilterRow,
  Toolbar,
} from "devextreme-react/data-grid";
import { Column } from "devextreme-react/tree-list";
import { exportDataGrid } from "devextreme/excel_exporter";
import { CalendarZoomLevel } from "devextreme/ui/calendar";
import notify from "../../lib/notify";
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  exportHourlySummaryByAppToXlsx,
  fetchHourlySummary,
  fetchHourlySummaryByApp,
} from "../../api/users";
import { useAuth } from "../../contexts/auth";
import { useCompanyContext } from "../../contexts/company";
import { useLoader } from "../../hooks/useLoading";
import { useScreenSize } from "../../utils/media-query";
import HourlySummaryByProject from "../hourly-summary-by-project/HourlySummaryByProject";

const getCompanyName = (id: number) => {
  switch (id) {
    case 1:
      return "Invenio";
    case 2:
      return "Invenio QD";
    case 3:
      return "Quality & Human Services";
    case 4:
      return "IPSUPPORT";
    default:
      return "";
  }
};

export const HourlySummary = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { companyId } = useCompanyContext();
  const loader = useLoader();
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;
  const [mobileSearch, setMobileSearch] = useState("");
  const [isExportingByApp, setIsExportingByApp] = useState(false);

  const [viewMode, setViewMode] = useState("ByApp");
  const [currentMonth, setCurrentMonth] = useState<string>(() => {
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const year = prevMonth.getFullYear();
    const month = String(prevMonth.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  });
  const [summary, setSummary] = useState<
    {
      employeeId: number;
      companyId: number;
      firstName: string;
      lastName: string;
      workInWeekend: number;
      workInHoliday: number;
      fiftyPrecentForHour: number;
      hundredPrecentForHour: number;
      workInNight: number;
      regularWorkHour: number;
    }[]
  >([]);
  const [summaryByApp, setSummaryByApp] = useState<
    {
      employeeId: number;
      companyId: number;
      firstName: string;
      lastName: string;
      workInWeekend: number;
      workInHoliday: number;
      regularWorkHour: number;
      totalHours: number;
    }[]
  >([]);
  // const [totalCount, setTotalCount] = useState(0);
  // const [pageSize, setPageSize] = useState(20);
  // const [pageIndex, setPageIndex] = useState(1);

  const filteredSummary = useMemo(() => {
    const q = mobileSearch.trim().toLowerCase();
    if (!q) return summary;
    return summary.filter((e) =>
      [e.lastName, e.firstName, getCompanyName(e.companyId)]
        .filter(Boolean)
        .some((v) => v.toString().toLowerCase().includes(q)),
    );
  }, [summary, mobileSearch]);

  const filteredSummaryByApp = useMemo(() => {
    const q = mobileSearch.trim().toLowerCase();
    if (!q) return summaryByApp;
    return summaryByApp.filter((e) =>
      [e.lastName, e.firstName, getCompanyName(e.companyId)]
        .filter(Boolean)
        .some((v) => v.toString().toLowerCase().includes(q)),
    );
  }, [summaryByApp, mobileSearch]);

  const exportByAppToExcel = async () => {
    if (isExportingByApp) return;
    setIsExportingByApp(true);
    loader?.showLoader();
    try {
      let company = 0;
      if (companyId === 0 || companyId === null || companyId === undefined) {
        company = user?.companyId || 0;
      } else {
        company = companyId;
      }

      await exportHourlySummaryByAppToXlsx(company, currentMonth);
    } catch (err) {
      notify(`${t("Error:")} ${err || t("Unknown error")}`, "error");
    } finally {
      loader?.hideLoader();
      setIsExportingByApp(false);
    }
  };

  const getHourlySummary = async () => {
    try {
      let company = 0;
      if (companyId === 0 || companyId === null || companyId === undefined) {
        company = user?.companyId || 0;
      } else {
        company = companyId;
      }

      const responseName = await fetchHourlySummary(
        company,
        // pageSize,
        // pageIndex,
        currentMonth,
      );

      if (responseName.data && Array.isArray(responseName.data)) {
        setSummary(responseName.data);
      } else {
        const namesList = responseName.data.data || [];
        setSummary(namesList);
      }
    } catch (err) {
      notify(`${t("Error:")} ${err || t("Unknown error")}`, "error");
    }
  };

  const getHourlySummaryByApp = async () => {
    try {
      let company = 0;
      if (companyId === 0 || companyId === null || companyId === undefined) {
        company = user?.companyId || 0;
      } else {
        company = companyId;
      }

      const responseName = await fetchHourlySummaryByApp(company, currentMonth);

      if (responseName.data && Array.isArray(responseName.data)) {
        setSummaryByApp(responseName.data);
      } else {
        const namesList = responseName.data.data || [];
        setSummaryByApp(namesList);
      }
    } catch (err) {
      notify(`${t("Error:")} ${err || t("Unknown error")}`, "error");
    }
  };

  // const handlePageClick = (newPageIndex) => {
  //   setPageIndex(newPageIndex);
  // };

  useEffect(() => {
    const fetchSummary = async () => {
      loader?.showLoader();
      try {
        if (viewMode === "General") {
          await getHourlySummary();
        } else if (viewMode === "ByApp") {
          await getHourlySummaryByApp();
        }
      } catch (error) {
        console.error("Error downloading hourly summary:", error);
      } finally {
        loader?.hideLoader();
      }
    };

    fetchSummary();
  }, [currentMonth, companyId, viewMode]);

  return (
    <ScrollView useNative>
      <div className={isMobile ? "p-4" : "p-6"}>
        <div className="mb-3 flex flex-col md:flex-row gap-2 justify-between items-start">
          <div>
            <div className="mb-2 text-2xl font-bold leading-none">
              {t("hourlySummary.header")}
            </div>
            <div className="text-base">{t("hourlySummary.headerText")}</div>
          </div>
          <SelectBox
            dataSource={[
              {
                value: "ByApp",
                text: t("hourlySummary.views.byApp.label"),
              },
              {
                value: "General",
                text: t("hourlySummary.views.general.label"),
              },
              {
                value: "AccordingToProjects",
                text: t("hourlySummary.views.accordingToProjects.label"),
              },
            ]}
            valueExpr="value"
            displayExpr="text"
            value={viewMode}
            onValueChanged={(e) => setViewMode(e.value)}
            labelMode="static"
            label={t("view")}
            width={isMobile ? "100%" : 350}
          />
        </div>
        {isMobile && viewMode !== "AccordingToProjects" && (
          <div className="sticky top-0 z-10 flex flex-col gap-2 py-2 bg-white dark:bg-[#1e1e1e]">
            <DateBox
              type="date"
              value={currentMonth}
              onValueChanged={(e) => setCurrentMonth(e.value)}
              displayFormat="MMM yyyy"
              openOnFieldClick
              calendarOptions={{
                maxZoomLevel: "year" as CalendarZoomLevel,
                minZoomLevel: "century" as CalendarZoomLevel,
              }}
              label={t("hourlySummary.views.general.dataGrid.month")}
              labelMode="static"
              width="100%"
            />
            <TextBox
              value={mobileSearch}
              onValueChanged={(e) => setMobileSearch(e.value || "")}
              placeholder={t("project.Search...")}
              showClearButton
              width="100%"
              mode="search"
            />
            {viewMode === "ByApp" && (
              <Button
                icon="exportxlsx"
                text={t("Export all")}
                type="success"
                stylingMode="contained"
                width="100%"
                disabled={isExportingByApp}
                onClick={exportByAppToExcel}
              />
            )}
          </div>
        )}
        {isMobile && viewMode === "General" && (
          <div className="flex flex-col gap-3 pb-4">
            {filteredSummary.length === 0 ? (
              <div
                className="p-4 text-sm text-blue-800 rounded-lg bg-blue-100 dark:bg-gray-800 dark:text-blue-400"
                role="alert"
              >
                {t("noSubordinateEmployees")}
              </div>
            ) : (
              filteredSummary.map((row) => (
                <div
                  key={`${row.companyId}-${row.employeeId}`}
                  className="rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#1e1e1e] shadow-sm overflow-hidden"
                >
                  <div className="px-4 pt-3 pb-2 border-b border-black/5 dark:border-white/5">
                    <div className="text-base font-bold break-words">
                      {`${row.lastName} ${row.firstName}`}
                    </div>
                    <div className="text-xs opacity-70">
                      {getCompanyName(row.companyId)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-2 p-4 text-sm">
                    <div className="flex flex-col">
                      <span className="text-[11px] uppercase opacity-70">
                        {t(
                          "hourlySummary.views.general.dataGrid.captions.regularWorkHour",
                        )}
                      </span>
                      <span className="font-semibold">
                        {row.regularWorkHour}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] uppercase opacity-70">
                        {t(
                          "hourlySummary.views.general.dataGrid.captions.workInWeekend",
                        )}
                      </span>
                      <span className="font-semibold">{row.workInWeekend}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] uppercase opacity-70">
                        {t(
                          "hourlySummary.views.general.dataGrid.captions.workInHoliday",
                        )}
                      </span>
                      <span className="font-semibold">{row.workInHoliday}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] uppercase opacity-70">
                        {t(
                          "hourlySummary.views.general.dataGrid.captions.workInNight",
                        )}
                      </span>
                      <span className="font-semibold">{row.workInNight}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] uppercase opacity-70">
                        {t(
                          "hourlySummary.views.general.dataGrid.captions.fiftyPrecentForHour",
                        )}
                      </span>
                      <span className="font-semibold">
                        {row.fiftyPrecentForHour}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] uppercase opacity-70">
                        {t(
                          "hourlySummary.views.general.dataGrid.captions.hundredPrecentForHour",
                        )}
                      </span>
                      <span className="font-semibold">
                        {row.hundredPrecentForHour}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        {isMobile && viewMode === "ByApp" && (
          <div className="flex flex-col gap-3 pb-4">
            {filteredSummaryByApp.length === 0 ? (
              <div
                className="p-4 text-sm text-blue-800 rounded-lg bg-blue-100 dark:bg-gray-800 dark:text-blue-400"
                role="alert"
              >
                {t("noSubordinateEmployees")}
              </div>
            ) : (
              filteredSummaryByApp.map((row) => (
                <div
                  key={`${row.companyId}-${row.employeeId}`}
                  className="rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#1e1e1e] shadow-sm overflow-hidden"
                >
                  <div className="px-4 pt-3 pb-2 border-b border-black/5 dark:border-white/5">
                    <div className="text-base font-bold break-words">
                      {`${row.lastName} ${row.firstName}`}
                    </div>
                    <div className="text-xs opacity-70">
                      {getCompanyName(row.companyId)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-2 p-4 text-sm">
                    <div className="flex flex-col">
                      <span className="text-[11px] uppercase opacity-70">
                        {t(
                          "hourlySummary.views.byApp.dataGrid.captions.totalHours",
                        )}
                      </span>
                      <span className="font-semibold">{row.totalHours}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] uppercase opacity-70">
                        {t(
                          "hourlySummary.views.byApp.dataGrid.captions.regularWorkHour",
                        )}
                      </span>
                      <span className="font-semibold">
                        {row.regularWorkHour}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] uppercase opacity-70">
                        {t(
                          "hourlySummary.views.byApp.dataGrid.captions.workInWeekend",
                        )}
                      </span>
                      <span className="font-semibold">{row.workInWeekend}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] uppercase opacity-70">
                        {t(
                          "hourlySummary.views.byApp.dataGrid.captions.workInHoliday",
                        )}
                      </span>
                      <span className="font-semibold">{row.workInHoliday}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        {!isMobile && viewMode === "General" && (
          <>
            <DataGrid
              dataSource={summary}
              keyExpr="employeeId"
              showBorders
              allowColumnResizing
              allowColumnReordering
              columnAutoWidth
              searchPanel={{
                visible: true,
                placeholder: t("project.Search..."),
              }}
              paging={{
                pageSize: 10,
              }}
              pager={{
                visible: true,
                showInfo: true,
                showNavigationButtons: true,
              }}
              export={{
                enabled: true,
                allowExportSelectedData: false,
                texts: {
                  exportAll: t("Export all"),
                  exportSelectedRows: t("Export selected"),
                },
              }}
              onExporting={async (e) => {
                const workbook = new ExcelJS.Workbook();
                const worksheet = workbook.addWorksheet(
                  t("hourlySummary.views.general.label"),
                );

                await exportDataGrid({
                  component: e.component,
                  worksheet,
                  autoFilterEnabled: true,
                });

                const buffer = await workbook.xlsx.writeBuffer();
                saveAs(
                  new Blob([buffer], { type: "application/octet-stream" }),
                  `PodsumowanieNaPodstawieWyplat_${new Date().toISOString().split("T")[0]}.xlsx`,
                );

                e.cancel = true;
              }}
            >
              <Toolbar>
                <DataGridItem
                  name="searchPanel"
                  location="before"
                  locateInMenu="auto"
                  cssClass="datagrid-search-panel-without-margin-left"
                />
                <DataGridItem
                  name="exportButton"
                  location="after"
                  options={{
                    text: "Eksportuj do Excel",
                    stylingMode: "contained",
                    elementAttr: {
                      class:
                        "bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-medium px-4 py-2 rounded-lg shadow-md transition duration-150 ease-in-out",
                    },
                  }}
                />
                <DataGridItem location="after" locateInMenu="auto">
                  <DateBox
                    type="date"
                    value={currentMonth}
                    onValueChanged={(e) => setCurrentMonth(e.value)}
                    displayFormat="MMM yyyy"
                    openOnFieldClick
                    calendarOptions={{
                      maxZoomLevel: "year" as CalendarZoomLevel,
                      minZoomLevel: "century" as CalendarZoomLevel,
                    }}
                    label={t("hourlySummary.views.general.dataGrid.month")}
                    labelMode="static"
                  />
                </DataGridItem>
              </Toolbar>
              <FilterRow visible />
              <Column
                dataField="lastName"
                dataType="string"
                caption={t(
                  "hourlySummary.views.general.dataGrid.captions.lastName",
                )}
                minWidth={isMobile ? 120 : 200}
                fixed
                fixedPosition="left"
              />
              <Column
                dataField="firstName"
                dataType="string"
                caption={t(
                  "hourlySummary.views.general.dataGrid.captions.firstName",
                )}
                minWidth={isMobile ? 120 : 200}
                fixed
                fixedPosition="left"
              />
              <Column
                dataField="companyId"
                dataType="string"
                caption={t("company")}
                customizeText={({ value }) => {
                  switch (value) {
                    case 1:
                      return "Invenio";
                    case 2:
                      return "Invenio QD";
                    case 3:
                      return "Quality & Human Services";
                    case 4:
                      return "IPSUPPORT";
                    default:
                      return "";
                  }
                }}
                minWidth={isMobile ? 120 : 150}
                fixed
                fixedPosition="left"
              />
              <Column
                dataField="workInWeekend"
                dataType="number"
                caption={t(
                  "hourlySummary.views.general.dataGrid.captions.workInWeekend",
                )}
                alignment="center"
                minWidth={150}
              />
              <Column
                dataField="workInHoliday"
                dataType="number"
                caption={t(
                  "hourlySummary.views.general.dataGrid.captions.workInHoliday",
                )}
                alignment="center"
                minWidth={150}
              />
              <Column
                dataField="fiftyPrecentForHour"
                dataType="number"
                caption={t(
                  "hourlySummary.views.general.dataGrid.captions.fiftyPrecentForHour",
                )}
                alignment="center"
                minWidth={150}
              />
              <Column
                dataField="hundredPrecentForHour"
                dataType="number"
                caption={t(
                  "hourlySummary.views.general.dataGrid.captions.hundredPrecentForHour",
                )}
                alignment="center"
                minWidth={150}
              />
              <Column
                dataField="workInNight"
                dataType="number"
                caption={t(
                  "hourlySummary.views.general.dataGrid.captions.workInNight",
                )}
                alignment="center"
                minWidth={150}
              />
              <Column
                dataField="regularWorkHour"
                dataType="number"
                caption={t(
                  "hourlySummary.views.general.dataGrid.captions.regularWorkHour",
                )}
                alignment="center"
                minWidth={150}
              />
            </DataGrid>
          </>
        )}
        {!isMobile && viewMode === "ByApp" && (
          <DataGrid
            dataSource={summaryByApp}
            keyExpr="employeeId"
            showBorders
            allowColumnResizing
            allowColumnReordering
            columnAutoWidth
            searchPanel={{
              visible: true,
              placeholder: t("project.Search..."),
            }}
            paging={{
              pageSize: 10,
            }}
            pager={{
              visible: true,
              showInfo: true,
              showNavigationButtons: true,
            }}
            export={{
              enabled: true,
              allowExportSelectedData: false,
              texts: {
                exportAll: t("Export all"),
                exportSelectedRows: t("Export selected"),
              },
            }}
            onExporting={async (e) => {
              e.cancel = true;
              await exportByAppToExcel();
            }}
          >
            <Toolbar>
              <DataGridItem
                name="searchPanel"
                location="before"
                locateInMenu="auto"
                cssClass="datagrid-search-panel-without-margin-left"
              />
              <DataGridItem
                name="exportButton"
                location="after"
                options={{
                  text: isExportingByApp
                    ? t("presence.exportToXlsProcessing")
                    : "Eksportuj do Excel",
                  stylingMode: "contained",
                  disabled: isExportingByApp,
                  elementAttr: {
                    class:
                      "bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-medium px-4 py-2 rounded-lg shadow-md transition duration-150 ease-in-out",
                  },
                }}
              />
              <DataGridItem location="after" locateInMenu="auto">
                <DateBox
                  type="date"
                  value={currentMonth}
                  onValueChanged={(e) => setCurrentMonth(e.value)}
                  displayFormat="MMM yyyy"
                  openOnFieldClick
                  calendarOptions={{
                    maxZoomLevel: "year" as CalendarZoomLevel,
                    minZoomLevel: "century" as CalendarZoomLevel,
                  }}
                  label={t("hourlySummary.views.byApp.dataGrid.month")}
                  labelMode="static"
                />
              </DataGridItem>
            </Toolbar>
            <FilterRow visible />
            <Column
              dataField="lastName"
              dataType="string"
              caption={t(
                "hourlySummary.views.byApp.dataGrid.captions.lastName",
              )}
              minWidth={isMobile ? 120 : 200}
              fixed
              fixedPosition="left"
            />
            <Column
              dataField="firstName"
              dataType="string"
              caption={t(
                "hourlySummary.views.byApp.dataGrid.captions.firstName",
              )}
              minWidth={isMobile ? 120 : 200}
              fixed
              fixedPosition="left"
            />
            <Column
              dataField="companyId"
              dataType="string"
              caption={t("company")}
              customizeText={({ value }) => {
                switch (value) {
                  case 1:
                    return "Invenio";
                  case 2:
                    return "Invenio QD";
                  case 3:
                    return "Quality & Human Services";
                  case 4:
                    return "IPSUPPORT";
                  default:
                    return "";
                }
              }}
              minWidth={isMobile ? 120 : 150}
              fixed
              fixedPosition="left"
            />
            <Column
              dataField="workInWeekend"
              dataType="number"
              caption={t(
                "hourlySummary.views.byApp.dataGrid.captions.workInWeekend",
              )}
              alignment="center"
              minWidth={150}
            />
            <Column
              dataField="workInHoliday"
              dataType="number"
              caption={t(
                "hourlySummary.views.byApp.dataGrid.captions.workInHoliday",
              )}
              alignment="center"
              minWidth={150}
            />
            <Column
              dataField="regularWorkHour"
              dataType="number"
              caption={t(
                "hourlySummary.views.byApp.dataGrid.captions.regularWorkHour",
              )}
              alignment="center"
              minWidth={150}
            />
            <Column
              dataField="totalHours"
              dataType="number"
              caption={t(
                "hourlySummary.views.byApp.dataGrid.captions.totalHours",
              )}
              alignment="center"
              minWidth={150}
            />
          </DataGrid>
        )}
        {viewMode === "AccordingToProjects" && <HourlySummaryByProject />}
      </div>
    </ScrollView>
  );
};
